import {emptyProductCombo} from '@/types/productComboTypes'

const transformComboSkus = comboSkus => {
  if (!Array.isArray(comboSkus)) {
    return comboSkus
  }

  return comboSkus.map(({productId, quantity}) => ({productId, quantity}))
}

const normalizeTextMap = value => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value
  }

  return {}
}

const normalizeSegmentConfiguration = segmentConfiguration => {
  if (!segmentConfiguration || typeof segmentConfiguration !== 'object') {
    return emptyProductCombo.segmentConfiguration
  }

  return {
    includedSegments: segmentConfiguration.includedSegments ?? null,
    excludedSegment: segmentConfiguration.excludedSegment ?? segmentConfiguration.excludedSegments ?? null,
  }
}

/**
 * Ensures combo save payloads use `excludedSegment` (API contract) and omit legacy `excludedSegments`.
 * @param {import('@/types/productComboTypes').ProductCombo['segmentConfiguration']} segmentConfiguration
 */
const normalizeSegmentConfigurationForSave = segmentConfiguration => {
  if (!segmentConfiguration || typeof segmentConfiguration !== 'object') {
    return segmentConfiguration
  }
  const {excludedSegments, excludedSegment, ...rest} = segmentConfiguration
  return {
    ...rest,
    excludedSegment: excludedSegment ?? excludedSegments ?? null,
  }
}

const normalizeComboDetailPayload = comboDetailResponse => {
  if (
    comboDetailResponse?.data &&
    typeof comboDetailResponse.data === 'object' &&
    !Array.isArray(comboDetailResponse.data)
  ) {
    return comboDetailResponse.data
  }

  return comboDetailResponse
}

const normalizeActiveState = comboDetailResponse => {
  if (typeof comboDetailResponse?.active === 'boolean') {
    return comboDetailResponse.active
  }

  const normalizedStatus = comboDetailResponse?.status?.toUpperCase?.()

  if (normalizedStatus === 'ACTIVE' || normalizedStatus === 'PUBLISHED') {
    return true
  }

  return false
}

/**
 * Normalizes combo save payloads before they are sent to the backend.
 * @param {import('@/types/productComboTypes').ProductCombo} productComboData
 * @returns {import('@/types/productComboTypes').ProductCombo}
 */
export const transformComboSaveRequest = productComboData => {
  const segmentConfiguration = productComboData?.segmentConfiguration
  return {
    ...productComboData,
    comboSkus: transformComboSkus(productComboData?.comboSkus),
    ...(segmentConfiguration !== undefined
      ? {segmentConfiguration: normalizeSegmentConfigurationForSave(segmentConfiguration)}
      : {}),
  }
}

/**
 * Normalizes combo detail API responses to the ProductCombo editor shape.
 * @param {import('@/types/productComboTypes').ProductComboDetailPayload | import('@/types/productComboTypes').ProductComboDetailApiResponse | undefined} comboDetailResponse
 * @returns {import('@/types/productComboTypes').ProductCombo}
 */
export const transformComboDetailResponse = comboDetailResponse => {
  const normalizedPayload = normalizeComboDetailPayload(comboDetailResponse)
  const createdDateTime =
    normalizedPayload?.createdDateTime ?? normalizedPayload?.createdDatetime ?? emptyProductCombo.createdDateTime
  const updatedDateTime =
    normalizedPayload?.updatedDateTime ?? normalizedPayload?.updatedDatetime ?? emptyProductCombo.updatedDateTime

  return {
    ...emptyProductCombo,
    name: normalizedPayload?.name ?? emptyProductCombo.name,
    productId: normalizedPayload?.productId ?? normalizedPayload?.externalId ?? emptyProductCombo.productId,
    ...(normalizedPayload?.salesOrg !== undefined ? {salesOrg: normalizedPayload.salesOrg} : {}),
    ...(normalizedPayload?.countryCode !== undefined ? {countryCode: normalizedPayload.countryCode} : {}),
    imageUrl: normalizedPayload?.imageUrl ?? emptyProductCombo.imageUrl,
    comboLimit: normalizedPayload?.comboLimit ?? emptyProductCombo.comboLimit,
    productLimit: normalizedPayload?.productLimit ?? emptyProductCombo.productLimit,
    displayCategoryIds: normalizedPayload?.displayCategoryIds ?? emptyProductCombo.displayCategoryIds,
    comboSkus: normalizedPayload?.comboSkus ?? emptyProductCombo.comboSkus,
    comboSubtitle: normalizeTextMap(
      normalizedPayload?.comboSubtitle ?? normalizedPayload?.shortDescription ?? emptyProductCombo.comboSubtitle
    ),
    comboDetails: normalizeTextMap(
      normalizedPayload?.comboDetails ?? normalizedPayload?.longDescription ?? emptyProductCombo.comboDetails
    ),
    comboDetails2: normalizeTextMap(
      normalizedPayload?.comboDetails2 ?? normalizedPayload?.comboLongDescription2 ?? emptyProductCombo.comboDetails2
    ),
    comboDescription: normalizeTextMap(
      normalizedPayload?.comboDescription ?? normalizedPayload?.productDescription ?? emptyProductCombo.comboDescription
    ),
    ...(createdDateTime !== undefined ? {createdDateTime} : {}),
    ...(updatedDateTime !== undefined ? {updatedDateTime} : {}),
    ...(normalizedPayload?.updatedBy !== undefined ? {updatedBy: normalizedPayload.updatedBy} : {}),
    ...(normalizedPayload?.status !== undefined ? {status: normalizedPayload.status} : {}),
    routeTypeId: normalizedPayload?.routeTypeId ?? emptyProductCombo.routeTypeId,
    active: normalizeActiveState(normalizedPayload),
    allCustomers: normalizedPayload?.allCustomers ?? emptyProductCombo.allCustomers,
    excludeSegment: normalizedPayload?.excludeSegment ?? emptyProductCombo.excludeSegment,
    isDraft: normalizedPayload?.isDraft ?? false,
    ignoreSkuList: normalizedPayload?.ignoreSkuList ?? emptyProductCombo.ignoreSkuList,
    segmentConfiguration: normalizeSegmentConfiguration(normalizedPayload?.segmentConfiguration),
    scope: normalizedPayload?.scope ?? emptyProductCombo.scope,
  }
}
